import { Persistence } from "../../config/constants";
import { TickLayerName } from "../../config/tickLayers";
import { BaseEntity } from "../../core/entity/BaseEntity";
import { Entity } from "../../core/entity/Entity";
import { on } from "../../core/entity/handler";
import { truckCabSideCell } from "../../game/lot";
import { cellCenter } from "../../game/player-motion";
import { Player } from "../entities/Player";
import { Clock } from "../singletons/Clock";
import { ShopInfo } from "../singletons/ShopInfo";
import { TimeFlow } from "../TimeFlow";

/**
 * The overnight: the NIGHT_MINUTES between driving home and waking up,
 * charged to a single engine tick — the same shape a drive's leg takes.
 *
 * Commands must never drive the engine (`game.step` from inside a
 * command would re-enter the tick), so waking up is split in two: the
 * `beginWakeUp` command validates and hands this system the night; this
 * system forces the night's minutes through TimeFlow, and the next
 * engine tick serves them whole to every sim layer in the order
 * `config/tickLayers.ts` sets — cures finish, a mid-browse customer
 * still buys, the street (which steps a long span minute by minute
 * itself) stays empty from close onward. `forceMinutes` bypasses the
 * pace model on purpose: the player home in bed is TimeFlow's hard stop,
 * and the overnight is precisely the minutes that pass anyway.
 *
 * This system runs on the "main" layer, after every sim layer of the
 * same tick, so once the clock has reached the morning the day lands in
 * one piece: the day number, a fresh `dayStartMinute`, the player back
 * beside the truck's cab, `away` cleared. Night checks during the batch
 * read the evening's `dayStartMinute`, so a player who slept early sees
 * the tail of the day pass before the street empties.
 *
 * **The night is atomic.** The batch and the morning's bookkeeping share
 * one engine tick, and saves are only ever taken between ticks, so a
 * snapshot shows one of exactly two shops — the evening before, asleep
 * at home on day D, or the morning after, awake beside the cab on day
 * D+1. Reloading the evening one simply runs the night again. Which
 * morning is coming is deliberately not persisted: a save taken between
 * a wake begun and its night served is still the evening.
 *
 * Transient — never serialized; added per session by the bootstrap.
 */
export class SleepSystem extends BaseEntity implements Entity {
  id = "sleepSystem";
  tickLayer: TickLayerName = "main";
  persistenceLevel: number = Persistence.Permanent;

  /** The shop minute morning lands on, while a night is underway. */
  private wakeMinute: number | null = null;

  /** Whether an overnight is underway (begun and not yet finished). */
  get overnightPending(): boolean {
    return this.wakeMinute !== null;
  }

  /** Charge an overnight of this many shop minutes. beginWakeUp's half. */
  begin(minutes: number): void {
    const clock = this.game.entities.getSingleton(Clock);
    const timeFlow = this.game.entities.getSingleton(TimeFlow);
    this.wakeMinute = clock.minute + minutes;
    timeFlow.forceMinutes(minutes);
  }

  @on("tick")
  onTick() {
    if (this.wakeMinute === null) {
      return;
    }
    const clock = this.game.entities.getSingleton(Clock);
    if (clock.minute < this.wakeMinute) {
      // Queued, not served yet: the night runs on the next engine tick.
      return;
    }
    // The night has run through every sim layer this tick (the "main"
    // layer comes after them all) — morning, all of it at once.
    this.wakeMinute = null;
    clock.day += 1;
    clock.dayStartMinute = clock.minute;
    const player = this.game.entities.getSingleton(Player);
    const shopInfo = this.game.entities.getSingleton(ShopInfo);
    player.away = null;
    player.position = cellCenter(truckCabSideCell(shopInfo.info));
    this.game.dispatch("playerChanged", {});
  }
}
